import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/UnifiedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SharedFooter from '../../components/SharedFooter';
import { Link } from 'react-router-dom';

const EngagementModelsFinder = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [showResults, setShowResults] = useState(false);

  // Interactive Q&A questions
  const questions = [
    {
      id: 1,
      question: "What is your primary objective for establishing a GCC?",
      options: [
        { value: 'cost_reduction', label: 'Cost reduction and operational efficiency' },
        { value: 'innovation_hub', label: 'Innovation and product development hub' },
        { value: 'market_expansion', label: 'Market expansion and local presence' },
        { value: 'talent_access', label: 'Access to specialized talent pool' }
      ]
    },
    {
      id: 2,
      question: "What is your expected timeline for GCC establishment?",
      options: [
        { value: 'urgent', label: 'Less than 6 months (urgent)' },
        { value: 'standard', label: '6-12 months (standard)' },
        { value: 'extended', label: '12-18 months (extended planning)' },
        { value: 'flexible', label: 'Flexible timeline based on optimal setup' }
      ]
    },
    {
      id: 3,
      question: "What is your preferred level of operational control?",
      options: [
        { value: 'full_control', label: 'Full direct control and ownership' },
        { value: 'shared_control', label: 'Shared control with local partner' },
        { value: 'managed_service', label: 'Fully managed service model' },
        { value: 'hybrid', label: 'Hybrid approach with flexible control levels' }
      ]
    },
    {
      id: 4,
      question: "What is your initial team size requirement?",
      options: [
        { value: 'small', label: '10-50 employees (small team)' },
        { value: 'medium', label: '50-200 employees (medium scale)' },
        { value: 'large', label: '200-500 employees (large scale)' },
        { value: 'enterprise', label: '500+ employees (enterprise scale)' }
      ]
    },
    {
      id: 5,
      question: "Which functions will your GCC primarily handle?",
      options: [
        { value: 'tech_dev', label: 'Technology development and engineering' },
        { value: 'business_ops', label: 'Business operations and support' },
        { value: 'analytics', label: 'Data analytics and business intelligence' },
        { value: 'multi_function', label: 'Multi-functional center of excellence' }
      ]
    }
  ];

  // Engagement models with recommendations
  const engagementModels = {
    build_operate_transfer: {
      name: 'Build-Operate-Transfer (BOT)',
      description: 'We establish and operate your GCC, then transfer full control once mature',
      bestFor: ['urgent', 'full_control', 'large', 'tech_dev'],
      timeline: '6-12 months to transfer',
      benefits: ['Fastest time to market', 'Expert operational setup', 'Full ownership transition'],
      considerations: ['Higher initial investment', 'Transfer learning curve']
    },
    joint_venture: {
      name: 'Joint Venture Partnership',
      description: 'Shared investment and control with local expertise and global oversight',
      bestFor: ['standard', 'shared_control', 'medium', 'multi_function'],
      timeline: '9-15 months',
      benefits: ['Shared risk and investment', 'Local market expertise', 'Flexible scaling'],
      considerations: ['Shared decision making', 'Partnership alignment required']
    },
    managed_services: {
      name: 'Fully Managed Services',
      description: 'Complete end-to-end management while you retain strategic control',
      bestFor: ['flexible', 'managed_service', 'small', 'business_ops'],
      timeline: '3-6 months',
      benefits: ['Lowest initial investment', 'Expert management', 'Focus on core business'],
      considerations: ['Less direct control', 'Ongoing service fees']
    },
    partnership_framework: {
      name: 'Partnership Framework',
      description: 'Strategic partnership with shared governance and operational excellence',
      bestFor: ['extended', 'hybrid', 'enterprise', 'analytics'],
      timeline: '12-18 months',
      benefits: ['Strategic alignment', 'Shared governance', 'Long-term partnership'],
      considerations: ['Complex structure', 'Longer setup time']
    }
  };

  const handleResponse = (value) => {
    setResponses({ ...responses, [questions[currentQuestion].id]: value });
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateRecommendation(value);
    }
  };

  const calculateRecommendation = (finalResponse) => {
    const allResponses = { ...responses, [questions[currentQuestion].id]: finalResponse };
    
    // Simple scoring algorithm based on response matching
    let scores = {};
    Object.keys(engagementModels).forEach(model => {
      scores[model] = 0;
      const bestFor = engagementModels[model].bestFor;
      Object.values(allResponses).forEach(response => {
        if (bestFor.includes(response)) {
          scores[model]++;
        }
      });
    });

    // Find highest scoring model
    const recommendedModel = Object.keys(scores).reduce((a, b) => 
      scores[a] > scores[b] ? a : b
    );

    setResponses({ ...allResponses, recommended: recommendedModel });
    setShowResults(true);
  };

  const resetQuestionnaire = () => {
    setCurrentQuestion(0);
    setResponses({});
    setShowResults(false);
  };

  const getProgressPercentage = () => {
    return ((currentQuestion + 1) / questions.length) * 100;
  };

  if (showResults) {
    const recommendedModel = engagementModels[responses.recommended];
    
    return (
      <>
        <Helmet>
          <title>Your Ideal Engagement Model Results | Bootminds</title>
          <meta name="description" content="Personalized GCC engagement model recommendation based on your specific requirements and objectives." />
        </Helmet>

        <div className="min-h-screen bg-background">
          <Header />
          
          <main className="pt-16 lg:pt-20">
            <section className="py-16 lg:py-20 bg-gradient-to-b from-background to-background-alt">
              <div className="container-executive">
                <div className="max-w-4xl mx-auto text-center space-y-8">
                  {/* Results Header */}
                  <div className="inline-flex items-center space-x-2 bg-success/10 text-success rounded-full px-6 py-3">
                    <Icon name="CheckCircle" size={20} color="currentColor" />
                    <span className="font-body-medium">Analysis Complete</span>
                  </div>
                  <h1 className="font-headline font-headline-bold text-3xl lg:text-5xl text-text-primary">
                    Your Recommended Engagement Model
                  </h1>

                  {/* Recommended Model Card */}
                  <div className="bg-card border border-border rounded-2xl p-8 mb-8">
                    <div className="text-center space-y-6">
                      <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
                        <Icon name="Award" size={32} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h2 className="font-headline font-headline-bold text-2xl text-text-primary mb-3">
                          {recommendedModel.name}
                        </h2>
                        <p className="text-text-secondary leading-relaxed max-w-2xl mx-auto">
                          {recommendedModel.description}
                        </p>
                      </div>
                      <div className="bg-background-alt rounded-xl p-4">
                        <p className="text-sm text-text-secondary">
                          <span className="font-body-medium text-text-primary">Expected Timeline: </span>
                          {recommendedModel.timeline}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Benefits and Considerations */}
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h3 className="font-headline font-headline-bold text-xl text-text-primary mb-4 flex items-center">
                        <Icon name="TrendingUp" size={20} color="var(--color-success)" className="mr-2" />
                        Key Benefits
                      </h3>
                      <ul className="space-y-3">
                        {recommendedModel.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5" />
                            <span className="text-text-secondary">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h3 className="font-headline font-headline-bold text-xl text-text-primary mb-4 flex items-center">
                        <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="mr-2" />
                        Key Considerations
                      </h3>
                      <ul className="space-y-3">
                        {recommendedModel.considerations.map((consideration, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <Icon name="Info" size={16} color="var(--color-warning)" className="mt-0.5" />
                            <span className="text-text-secondary">{consideration}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className="bg-muted/30 border border-border rounded-2xl p-8 text-center space-y-6">
                    <h3 className="font-headline font-headline-bold text-2xl text-text-primary">
                      Next Steps
                    </h3>
                    <p className="text-text-secondary max-w-2xl mx-auto">
                      Schedule a consultation with our experts to discuss your personalized engagement model 
                      and develop a detailed implementation roadmap.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        variant="default"
                        size="lg"
                        className="bg-cta text-cta-foreground"
                        iconName="Calendar"
                        iconPosition="left"
                        asChild
                      >
                        <Link to="/executive-contact-strategic-engagement">
                          Book Expert Consultation
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="lg"
                        className="border-primary text-primary bg-background"
                        onClick={resetQuestionnaire}
                        iconName="RotateCcw"
                        iconPosition="left"
                      >
                        Retake Assessment
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>

          <SharedFooter variant="default" />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Find Your Ideal Engagement Model - Interactive Assessment | Bootminds</title>
        <meta 
          name="description" 
          content="Interactive Q&A tool to determine the optimal GCC engagement model for your organization. Get personalized recommendations based on your specific requirements." 
        />
        <meta 
          name="keywords" 
          content="engagement model finder, GCC assessment, interactive questionnaire, partnership model, BOT model" 
        />
        <link rel="canonical" href="/assessment" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 lg:py-20">
          {/* Hero Section */}
          <section className="py-16 lg:py-20 bg-gradient-to-b from-background to-background-alt">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <h1 className="font-headline font-headline-bold text-3xl lg:text-5xl text-text-primary">
                  Find Your Ideal Engagement Model
                </h1>
                <p className="text-lg text-text-secondary leading-relaxed">
                  Interactive Q&A assessment to identify the optimal engagement model for your Global Capability Center. 
                  Get personalized recommendations based on your specific requirements and objectives.
                </p>
              </div>
            </div>
          </section>

          {/* Progress Bar */}
          <section className="py-4 bg-card border-b border-border">
            <div className="container-executive">
              <div className="max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-body-medium text-text-primary">
                    Question {currentQuestion + 1} of {questions.length}
                  </span>
                  <span className="text-sm text-text-secondary">
                    {Math.round(getProgressPercentage())}% Complete
                  </span>
                </div>
                <div className="w-full bg-background rounded-full h-2">
                  <div 
                    className="bg-accent h-2 rounded-full transition-all duration-300"
                    style={{ width: `${getProgressPercentage()}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </section>

          {/* Question Section */}
          <section className="py-16">
            <div className="container-executive">
              <div className="max-w-2xl mx-auto">
                <div className="bg-card border border-border rounded-2xl p-8 space-y-8">
                  <div className="text-center">
                    <h2 className="font-headline font-headline-bold text-2xl text-text-primary mb-4">
                      {questions[currentQuestion].question}
                    </h2>
                    <p className="text-text-secondary">
                      Select the option that best describes your requirements
                    </p>
                  </div>

                  <div className="space-y-4">
                    {questions[currentQuestion].options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleResponse(option.value)}
                        className="w-full p-4 text-left rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all duration-300 group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-6 h-6 border-2 border-border rounded-full group-hover:border-primary transition-colors duration-300 flex items-center justify-center">
                            <div className="w-2 h-2 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                          <span className="font-body text-text-primary group-hover:text-primary">
                            {option.label}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-between pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                      disabled={currentQuestion === 0}
                      iconName="ChevronLeft"
                      iconPosition="left"
                    >
                      Previous
                    </Button>
                    
                    <div className="text-xs text-text-secondary self-center">
                      Select an option to continue
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Info Section */}
          <section className="py-12 bg-background-alt">
            <div className="container-executive">
              <div className="max-w-4xl mx-auto text-center space-y-8">
                <h3 className="font-headline font-headline-bold text-2xl text-text-primary">
                  Why Use Our Engagement Model Finder?
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon name="Target" size={24} color="var(--color-primary)" />
                    </div>
                    <h4 className="font-body font-body-medium text-text-primary mb-2">Personalized</h4>
                    <p className="text-sm text-text-secondary">Tailored recommendations based on your specific needs</p>
                  </div>
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon name="Zap" size={24} color="var(--color-accent)" />
                    </div>
                    <h4 className="font-body font-body-medium text-text-primary mb-2">Instant Results</h4>
                    <p className="text-sm text-text-secondary">Immediate analysis and detailed recommendations</p>
                  </div>
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon name="Award" size={24} color="var(--color-success)" />
                    </div>
                    <h4 className="font-body font-body-medium text-text-primary mb-2">Expert-Backed</h4>
                    <p className="text-sm text-text-secondary">Based on 5+ successful GCC implementations</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <SharedFooter variant="default" />
      </div>
    </>
  );
};

export default EngagementModelsFinder;